import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import usersTableData from "layouts/users/data/usersTableData";
import { useEffect, useMemo, useState } from "react";
import AlertBox from "components/Alert/alert";
import { allUsers } from "services/apiService";
import { unBlockUser } from "services/apiService";
import { blockUser } from "services/apiService";
import { deleteUser } from "services/apiService";
import { setUploadAccess } from "services/apiService";
import Loader from "components/Loader/loader";
import { setUploadBulkAccess } from "services/apiService";
import PermissionsDialog from "components/Permission/permission";
import { getAllIndexes } from "services/apiService";

function UserManagement() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    isDelete: false,
    userId: "",
  });
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    getAllUsers();
  }, [pageCount]);


  useEffect(() => {
    getIndex();
  }, []);

  const getIndex = async () => {
    try {
      const response = await getAllIndexes();
      if (response?.success) {
        setPermissions(response?.data)
      }
    } catch (err) {
      console.error("Fetch indexes failed:", err);
    }
  };

  const getAllUsers = async () => {
    try {
      const response = await allUsers();
      if (response?.success) {
        const usersVerified = response?.data?.users
          .filter((u) => u.isVerified)
        setPeople(usersVerified);
        const totalPages = Math.ceil(response?.data?.users?.length / pageCount);
        if (page > totalPages) setPage(1);
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    }
  };

  const handleSelectUser = async (data) => {
    setLoading(true);
    const payload = {
      userId: data?.id,
    };

    try {
      const response = await setUploadAccess(payload);
      if (response?.success) {
        setSelectedUserId(null);
        setSelectedPermissions({});
        setOpen(false);

        getAllUsers();

        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: response?.message?.message,
          isDelete: false,
          userId: response?.message?.name || "Twister User",
        });
      } else {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: response?.message?.message || "Something went wrong",
          isDelete: false,
          userId: response?.message?.name || "Twister User",
        });
      }
    } catch (error) {
      console.error("Error updating upload access:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = async () => {
    const entriesPerPage = pageCount;

    const startIndex = (page - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;

    const currentPageUsers = people.slice(startIndex, endIndex);

    const allSelectedOnPage = currentPageUsers.every((p) => selectedUsers.includes(p.id));

    let newSelectedUsers = [...selectedUsers];
    let payload = {};

    if (allSelectedOnPage) {
      newSelectedUsers = selectedUsers.filter((id) => !currentPageUsers.some((p) => p.id === id));
      payload = {
        userIds: currentPageUsers.map((p) => p.id),
        hasUploadAccess: false,
      };
    } else {
      const usersToAdd = currentPageUsers
        .filter((p) => !selectedUsers.includes(p.id))
        .map((p) => p.id);

      newSelectedUsers = [...selectedUsers, ...usersToAdd];
      payload = {
        userIds: usersToAdd,
        // hasUploadAccess: true,
      };
    }

    setSelectedUsers(newSelectedUsers);

    try {
      const response = await setUploadBulkAccess(payload);
      if (response?.success) {
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: response?.message?.message,
          isDelete: false,
          userId: response?.message?.name,
        });
        getAllUsers();
      }
    } catch (error) {
      console.error("Error updating bulk upload access:", error);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    const { isDelete, userId } = alert;
    const action = isDelete ? deleteUser : blockUser;
    const actionType = isDelete ? "deleted" : "blocked";

    try {
      const res = await action({ userId });

      const alertType = res?.error ? "error" : "success";
      const alertTitle = res?.error ? "Oops!" : "Success";
      const alertMessage = res?.message || `User successfully ${actionType}.`;

      setAlert({
        open: true,
        type: alertType,
        title: alertTitle,
        message: alertMessage,
        isDelete: false,
        userId: "",
      });

      if (!res?.error) {
        getAllUsers();
      }
    } catch (err) {
      console.error("Action failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isDelete ? "delete" : "block"} user.`,
        isDelete: false,
        userId: "",
      });
    }
  };

  const handleDelete = async (userId) => {
    setAlert({
      open: true,
      type: "confirm",
      title: "Confirm Delete",
      message: "Are you sure you want to delete this user?",
      isDelete: true,
      userId: userId,
    });
  };

  const handleToggleBlock = async (userId, currentStatus) => {
    if (currentStatus) {
      setAlert({
        open: true,
        type: "confirm",
        title: "Confirm Block",
        message: "Are you sure you want to block this user?",
        isDelete: false,
        userId: userId,
      });
    } else {
      try {
        let obj = {
          userId: userId,
        };
        const res = await unBlockUser(obj);
        if (res?.error) {
          setAlert({
            open: true,
            type: "error",
            title: "Opps!",
            message: res?.message,
            isDelete: false,
            userId: userId,
          });
        } else {
          setAlert({
            open: true,
            type: "success",
            title: "Success",
            message: res?.message,
            isDelete: false,
            userId: "",
          });
          getAllUsers();
        }
      } catch (err) {
        console.error("Update status failed:", err);
      }
    }
  };

  const handleAllowPermission = (userId, hasUploadAccess) => {
    setSelectedUserId(userId);
    setSelectedPermissions(hasUploadAccess);
    // setOpen(true);
    handleSelectUser(userId)
  }

  const entriesPerPage = pageCount;
  const startIndex = (page - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentPageUsers = people.slice(startIndex, endIndex);

  const { columns, rows } = useMemo(() => {
    return usersTableData({
      peopleList: people,
      currentPageUsers,
      onDelete: handleDelete,
      onToggleBlock: handleToggleBlock,
      onAllowPermission: handleAllowPermission,
      onSelectAll: handleSelectAll,
      onSelectUser: handleSelectUser,
      selectedUsers,
    });
  }, [people, page, pageCount,]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          onConfirm();
          setAlert({ ...alert, open: false });
        }}
      />

      <PermissionsDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedUserId(null);
          setSelectedPermissions({});
        }}
        permissionsList={permissions}
        selectedPermissions={selectedPermissions}
        onSubmit={(data) => {
          const hasChanges = Object.keys(data).length > 0;

          if (!hasChanges) {
            setOpen(false);
            return;
          }
          handleSelectUser(data);
        }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Users
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                  pageEntryCount={setPageCount}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserManagement;
